import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { toast } from 'react-toastify';
import { FiCamera, FiCheck, FiX, FiClock, FiUsers, FiLogIn, FiLogOut } from 'react-icons/fi';
import { staffAttendanceAPI, holidayAPI } from '../services/api';

const StaffAttendanceScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [scanner, setScanner] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [scanMode, setScanMode] = useState('login'); // 'login' or 'logout'
  const [todayStats, setTodayStats] = useState({
    total: 0,
    logged_in: 0,
    logged_out: 0
  });
  const [holidayInfo, setHolidayInfo] = useState(null);

  useEffect(() => {
    fetchTodayStats();
    checkHoliday();
  }, []);

  useEffect(() => {
    return () => {
      if (scanner) {
        scanner.clear().catch(err => console.error('Error clearing scanner:', err));
      }
    };
  }, [scanner]);

  const fetchTodayStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await staffAttendanceAPI.getStaffForScanner({ date: today });
      if (response.data.success) {
        const staff = response.data.data;
        setTodayStats({
          total: staff.length,
          logged_in: staff.filter(s => s.has_logged_in && !s.has_logged_out).length,
          logged_out: staff.filter(s => s.has_logged_out).length
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const checkHoliday = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await holidayAPI.checkHoliday({ date: today });
      if (response.data.success && response.data.data.is_holiday) {
        setHolidayInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error checking holiday:', error);
    }
  };

  const startScanning = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      
      const html5QrcodeScanner = new Html5QrcodeScanner(
        "qr-reader",
        { 
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true
        },
        false
      );

      html5QrcodeScanner.render(onScanSuccess, onScanError);
      setScanner(html5QrcodeScanner);
      setScanning(true);
      toast.success('Camera started successfully!');
    } catch (error) {
      console.error('Camera permission error:', error);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else if (error.name === 'NotReadableError') {
        toast.error('Camera is already in use by another application.');
      } else if (error.name === 'SecurityError') {
        toast.error('Camera access requires HTTPS. Please use https:// or localhost.');
      } else {
        toast.error('Error accessing camera: ' + error.message);
      }
    }
  };

  const stopScanning = () => {
    if (scanner) {
      scanner.clear().then(() => {
        setScanning(false);
        setScanner(null);
      }).catch(err => {
        console.error('Error stopping scanner:', err);
      });
    }
  };

  const onScanSuccess = async (decodedText, decodedResult) => {
    try {
      console.log('Scanned QR Data:', decodedText);
      const qrData = JSON.parse(decodedText);
      console.log('Parsed QR Data:', qrData);
      
      if (qrData.type !== 'staff_attendance') {
        toast.error(`Invalid QR code type - Expected 'staff_attendance', got '${qrData.type}'`);
        console.error('QR Type mismatch:', qrData);
        return;
      }

      playBeep();

      const response = await staffAttendanceAPI.scanAttendance({
        staff_id: qrData.staff_id,
        date: new Date().toISOString().split('T')[0],
        scan_type: scanMode
      });

      const data = response.data;

      if (data.success) {
        const staffData = data.data;
        const status = data.status;
        
        setRecentScans(prev => [{
          ...staffData,
          scanned_at: new Date().toLocaleTimeString(),
          scan_type: scanMode,
          status: status,
          login_time: data.login_time,
          logout_time: data.logout_time,
          total_hours: data.total_hours,
          is_holiday: data.is_holiday,
          holiday_title: data.holiday_title
        }, ...prev.slice(0, 9)]);

        fetchTodayStats();

        if (status === 'logged_in') {
          toast.success(`✓ ${staffData.name} ${staffData.surname} - Logged In${data.is_holiday ? ' (Holiday Work)' : ''}`);
        } else if (status === 'logged_out') {
          toast.success(`🚪 ${staffData.name} ${staffData.surname} - Logged Out (${data.total_hours}h)`);
        } else if (status === 'already_logged_in') {
          toast.info(`ℹ ${staffData.name} ${staffData.surname} - Already Logged In Today`);
        } else if (status === 'already_logged_out') {
          toast.info(`ℹ ${staffData.name} ${staffData.surname} - Already Logged Out Today`);
        }
      } else {
        toast.error(data.message);
        playErrorBeep();
      }
    } catch (error) {
      console.error('Scan error:', error);
      if (error instanceof SyntaxError) {
        toast.error('Invalid QR code format - Not valid JSON');
        console.error('QR Parse Error:', decodedText);
      } else {
        toast.error(error.message || 'Error marking attendance');
      }
      playErrorBeep();
    }
  };

  const onScanError = (errorMessage) => {
    // Ignore continuous scanning errors
  };

  const playBeep = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  };

  const playErrorBeep = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 400;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Staff QR Scanner</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Scan staff ID cards for login/logout attendance</p>
        </div>
      </div>

      {/* Holiday Banner */}
      {holidayInfo && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">🎉</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-semibold text-yellow-800">
                TODAY IS A HOLIDAY - {holidayInfo.title}
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Staff scanning will be marked as "Holiday Work"
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Staff</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats.total}</p>
            </div>
            <FiUsers className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Logged In</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats.logged_in}</p>
            </div>
            <FiLogIn className="text-green-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Logged Out</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats.logged_out}</p>
            </div>
            <FiLogOut className="text-purple-500" size={32} />
          </div>
        </div>
      </div>

      {/* Scanner Mode Toggle */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Scanner Mode</h3>
        <div className="flex gap-4">
          <button
            onClick={() => setScanMode('login')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold transition-all ${
              scanMode === 'login'
                ? 'bg-green-600 text-white shadow-lg scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <FiLogIn size={24} />
            LOGIN (Check-In)
          </button>
          <button
            onClick={() => setScanMode('logout')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold transition-all ${
              scanMode === 'logout'
                ? 'bg-purple-600 text-white shadow-lg scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <FiLogOut size={24} />
            LOGOUT (Check-Out)
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3 text-center">
          Current Mode: <strong className={scanMode === 'login' ? 'text-green-600' : 'text-purple-600'}>
            {scanMode === 'login' ? 'LOGIN' : 'LOGOUT'}
          </strong>
        </p>
      </div>

      {/* Scanner Section */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Camera Scanner</h3>
          {scanning ? (
            <button
              onClick={stopScanning}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiX /> Stop Scanner
            </button>
          ) : (
            <button
              onClick={startScanning}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiCamera /> Start Scanner
            </button>
          )}
        </div>

        <div id="qr-reader" className="w-full max-w-md mx-auto"></div>

        {!scanning && (
          <div className="text-center py-12">
            <FiCamera size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-600 mb-2">Click "Start Scanner" to begin scanning staff ID cards</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 max-w-md mx-auto text-left">
              <p className="text-sm font-semibold text-blue-900 mb-2">📷 Camera Permission Required</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Your browser will ask for camera permission</li>
                <li>• Click "Allow" when prompted</li>
                <li>• If blocked, check browser address bar for camera icon</li>
                <li>• For localhost, camera should work without HTTPS</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Recent Scans */}
      {recentScans.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Scans</h3>
            <button
              onClick={() => setRecentScans([])}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear
            </button>
          </div>

          <div className="space-y-2">
            {recentScans.map((scan, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${
                  scan.status === 'logged_in' || scan.status === 'logged_out'
                    ? 'bg-green-50 border-green-500' 
                    : 'bg-blue-50 border-blue-500'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${
                    scan.scan_type === 'login' ? 'bg-green-600' : 'bg-purple-600'
                  } text-white rounded-full flex items-center justify-center font-semibold`}>
                    {scan.scan_type === 'login' ? <FiLogIn /> : <FiLogOut />}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {scan.name} {scan.surname}
                      {scan.is_holiday && <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">🎉 Holiday Work</span>}
                    </p>
                    <p className="text-xs text-gray-600">
                      {scan.role_name} | Employee: {scan.employee_id}
                    </p>
                    {scan.total_hours && (
                      <p className="text-xs text-gray-600 mt-1">
                        <FiClock className="inline mr-1" />
                        Working Hours: <strong>{scan.total_hours}h</strong>
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{scan.scanned_at}</p>
                  <span className={`text-xs font-medium ${
                    scan.status === 'logged_in' ? 'text-green-600' :
                    scan.status === 'logged_out' ? 'text-purple-600' :
                    'text-blue-600'
                  }`}>
                    {scan.status === 'logged_in' && '✓ Logged In'}
                    {scan.status === 'logged_out' && '🚪 Logged Out'}
                    {scan.status === 'already_logged_in' && 'ℹ Already In'}
                    {scan.status === 'already_logged_out' && 'ℹ Already Out'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffAttendanceScanner;
